import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useWebSocket } from "@/contexts/websocket-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { 
  Clock, 
  Play,
  Pause,
  RefreshCw,
  Database,
  FileText,
  Archive,
  Settings,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Ban,
  Timer,
  Zap,
  Activity,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

interface BackupSettings {
  id: number;
  enabled: boolean;
  windowStartHour: number;
  windowStartMinute: number;
  windowEndHour: number;
  windowEndMinute: number;
  jitterMinutes: number;
  maxLagMs: number;
  dbBackupEnabled: boolean;
  htmlBackupEnabled: boolean;
  zipBackupEnabled: boolean;
  dbRetentionDays: number;
  dbRetentionWeeks: number;
  htmlHistoryKeep: number;
  lastRunAt: string | null;
  nextScheduledAt: string | null;
  updatedAt: string;
  updatedBy: number | null;
  windowDescription?: string;
  nextRunFormatted?: string;
}

interface QueueStats {
  pending: number;
  running: number;
  completedToday: number;
  failedToday: number;
}

interface QueueStatus {
  queue: QueueStats;
  currentlyRunning: {
    id: number;
    backupType: string;
    projectId: string | null;
    startedAt: string;
  } | null;
  lastRunAt: string | null;
  nextScheduledAt: string | null;
  nextRunFormatted?: string;
}

interface ImageCacheStats {
  cached: number;
  reUploaded: number;
  downloaded: number;
  gc: number;
  errors: number;
}

interface QueueItem {
  id: number;
  backupType: 'database' | 'html' | 'zip' | 'archive';
  projectId: string | null;
  priority?: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'success' | 'skipped';
  triggeredBy?: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  queuedAt: string;
  source?: 'orchestrator' | 'manual';
  metadata?: { imageCacheStats?: ImageCacheStats; [k: string]: any } | null;
}

function formatTimeWindow(startHour: number, startMinute: number, endHour: number, endMinute: number): string {
  const formatTime = (h: number, m: number) => 
    `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  return `${formatTime(startHour, startMinute)} - ${formatTime(endHour, endMinute)} CET`;
}

function QueueStatusBadge({ status }: { status: QueueItem['status'] }) {
  const configs: Record<string, { label: string; icon: typeof Clock; className: string }> = {
    pending: { label: 'Pending', icon: Clock, className: 'border-yellow-500 text-yellow-400' },
    running: { label: 'Running', icon: Loader2, className: 'border-blue-500 text-blue-400 animate-pulse' },
    completed: { label: 'Completed', icon: CheckCircle2, className: 'border-green-500 text-green-400' },
    success: { label: 'Success', icon: CheckCircle2, className: 'border-green-500 text-green-400' },
    failed: { label: 'Failed', icon: XCircle, className: 'border-red-500 text-red-400' },
    cancelled: { label: 'Cancelled', icon: Ban, className: 'border-gray-500 text-gray-400' },
    skipped: { label: 'Skipped', icon: AlertTriangle, className: 'border-orange-500 text-orange-400' },
  };
  
  const config = configs[status] || { label: status, icon: AlertTriangle, className: 'border-gray-500 text-gray-400' };
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`text-xs ${config.className}`}>
      <Icon className={`h-3 w-3 mr-1 ${status === 'running' ? 'animate-spin' : ''}`} />
      {config.label}
    </Badge>
  );
}

function BackupTypeBadge({ type }: { type: QueueItem['backupType'] | string }) {
  const configs: Record<string, { label: string; icon: typeof Clock; className: string }> = {
    database: { label: 'DB', icon: Database, className: 'bg-purple-500/20 text-purple-300 border-purple-500/50' },
    html: { label: 'HTML', icon: FileText, className: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
    zip: { label: 'ZIP', icon: Archive, className: 'bg-orange-500/20 text-orange-300 border-orange-500/50' },
    archive: { label: 'Archive', icon: Archive, className: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50' },
  };
  
  const config = configs[type] || { label: type || 'Unknown', icon: AlertTriangle, className: 'bg-gray-500/20 text-gray-300 border-gray-500/50' };
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`text-xs ${config.className}`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
}

export function AutomatedBackupSettings() {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editedSettings, setEditedSettings] = useState<Partial<BackupSettings>>({});

  const { data: settings, isLoading: settingsLoading, refetch: refetchSettings } = useQuery<BackupSettings>({
    queryKey: ['/api/admin/backup-settings'],
  });

  const { data: queueStatus, refetch: refetchQueue } = useQuery<QueueStatus>({
    queryKey: ['/api/admin/backup-queue/status'],
    refetchInterval: 5000,
  });

  // HTML backup cache health (task #216)
  interface CacheHealth {
    totals: { projects: number; fresh: number; pending: number; overdue: number; failed: number };
    coverage: { cachedFragments: number; totalPulses: number; pct: number | null };
    unhealthy: Array<{
      projectId: string;
      projectName: string | null;
      tier: 'fresh' | 'pending' | 'overdue' | 'failed';
      reason: 'last_backup_failed' | 'queue_stuck' | 'overdue_dirty' | 'never_backed_up' | null;
      lastActivityAt: string | null;
      lastHtmlBackupAt: string | null;
      lastSuccessfulBackupAt: string | null;
      lastErrorMessage: string | null;
      hasStuckQueue: boolean;
      coverage: { cached: number; total: number };
      coveragePct: number | null;
    }>;
  }
  const { data: cacheHealth } = useQuery<CacheHealth>({
    queryKey: ['/api/admin/html-backup/cache-health'],
    refetchInterval: 30_000,
  });

  // Refresh cache-health on WebSocket backup events instead of waiting for the
  // 30s poll (#216 review).
  const { subscribeToProgress, unsubscribeFromProgress } = useWebSocket();
  useEffect(() => {
    if (!subscribeToProgress || !unsubscribeFromProgress) return;
    const subId = 'admin-cache-health-refresh';
    subscribeToProgress(subId, (msg: any) => {
      if (
        msg.type === 'backup:status_changed' ||
        msg.type === 'backup:queue_updated' ||
        msg.stage === 'complete' ||
        msg.stage === 'error'
      ) {
        queryClient.invalidateQueries({ queryKey: ['/api/admin/html-backup/cache-health'] });
        queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/status'] });
      }
    });
    return () => unsubscribeFromProgress(subId);
  }, [subscribeToProgress, unsubscribeFromProgress]);

  const reasonLabel = (r: string | null): string => {
    switch (r) {
      case 'last_backup_failed': return 'Last backup failed';
      case 'queue_stuck': return 'Queue stuck >2h';
      case 'overdue_dirty': return 'Dirty >48h';
      case 'never_backed_up': return 'Never backed up';
      default: return '';
    }
  };

  const { data: queueHistory, isLoading: historyLoading } = useQuery<QueueItem[]>({
    queryKey: ['/api/admin/backup-queue/history'],
    refetchInterval: 10000,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (updates: Partial<BackupSettings>) => {
      return apiRequest('PATCH', '/api/admin/backup-settings', updates);
    },
    onSuccess: () => {
      toast({
        title: "Settings updated",
        description: "Automated backup settings have been saved.",
      });
      setIsEditing(false);
      setEditedSettings({});
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-settings'] });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error.message || "Could not update settings.",
        variant: "destructive",
      });
    },
  });

  const triggerBackupMutation = useMutation({
    mutationFn: async ({ backupType, projectId }: { backupType: string; projectId?: string }) => {
      return apiRequest('POST', '/api/admin/backup-queue/trigger', { backupType, projectId });
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Backup queued",
        description: `${variables.backupType.toUpperCase()} backup added to queue.`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/status'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/history'] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to queue backup",
        description: error.message || "Could not add backup to queue.",
        variant: "destructive",
      });
    },
  });

  const processQueueMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', '/api/admin/backup-queue/process-queue', { continueOnError: true });
    },
    onSuccess: async (response) => {
      const data = await response.json();
      if (data.processed === 0) {
        toast({
          title: "Nothing to process",
          description: "Queue is empty or locked.",
        });
      } else if (data.failed === 0) {
        toast({
          title: "Queue processed",
          description: `${data.succeeded} backup(s) completed successfully.`,
        });
      } else {
        toast({
          title: "Queue processed with errors",
          description: `${data.succeeded} succeeded, ${data.failed} failed.`,
          variant: data.succeeded > 0 ? "default" : "destructive",
        });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/status'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/history'] });
    },
    onError: (error: any) => {
      toast({
        title: "Queue processing failed",
        description: error.message || "Could not process queue.",
        variant: "destructive",
      });
    },
  });

  const cancelAllMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', '/api/admin/backup-queue/cancel-all');
    },
    onSuccess: async (response) => {
      const data = await response.json();
      toast({
        title: "Queue cleared",
        description: data.message || "All pending backups cancelled.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/status'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/history'] });
    },
    onError: (error: any) => {
      toast({
        title: "Cancel failed",
        description: error.message || "Could not cancel backups.",
        variant: "destructive",
      });
    },
  });

  const triggerHtmlAllMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', '/api/admin/backup-queue/trigger-html-all');
    },
    onSuccess: async (response) => {
      const data = await response.json();
      toast({
        title: "HTML backups queued",
        description: data.message || `Queued ${data.queued} HTML backups.`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/status'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backup-queue/history'] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to queue HTML backups",
        description: error.message || "Could not queue HTML backups.",
        variant: "destructive",
      });
    },
  });

  const handleToggleEnabled = async () => {
    if (!settings) return;
    await updateSettingsMutation.mutateAsync({ enabled: !settings.enabled });
  };

  const handleSaveSettings = async () => {
    if (Object.keys(editedSettings).length === 0) {
      setIsEditing(false);
      return;
    }
    await updateSettingsMutation.mutateAsync(editedSettings);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedSettings({});
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const currentSettings = { ...settings, ...editedSettings };

  return (
    <div className="space-y-4">
      <Card className="bg-slate-900 border-slate-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle className="text-base">Automated Backup Schedule</CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Nightly backups run automatically during off-peak hours
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Label htmlFor="backup-enabled" className="text-xs text-muted-foreground">
                  {settings?.enabled ? 'Enabled' : 'Disabled'}
                </Label>
                <Switch
                  id="backup-enabled"
                  data-testid="switch-backup-enabled"
                  checked={settings?.enabled ?? false}
                  onCheckedChange={handleToggleEnabled}
                  disabled={updateSettingsMutation.isPending}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <Separator className="bg-slate-700" />
        <CardContent className="pt-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs mb-1">Backup Window</p>
              <p className="font-medium">
                {settings ? formatTimeWindow(
                  currentSettings.windowStartHour ?? 2,
                  currentSettings.windowStartMinute ?? 30,
                  currentSettings.windowEndHour ?? 6,
                  currentSettings.windowEndMinute ?? 0
                ) : '--'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-1">Next Run</p>
              <p className="font-medium">
                {settings?.nextRunFormatted || queueStatus?.nextRunFormatted || 'Not scheduled'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-1">Last Run</p>
              <p className="font-medium">
                {settings?.lastRunAt 
                  ? formatDistanceToNow(new Date(settings.lastRunAt), { addSuffix: true })
                  : 'Never'}
              </p>
            </div>
          </div>

          <Separator className="my-4 bg-slate-700" />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Backup Types Enabled</span>
              {!isEditing && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsEditing(true)}
                  data-testid="button-edit-settings"
                >
                  Edit
                </Button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className={`p-3 rounded-lg border ${currentSettings.dbBackupEnabled ? 'border-purple-500/50 bg-purple-500/10' : 'border-slate-700 bg-slate-800/50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Database className="h-4 w-4 text-purple-400" />
                  <span className="text-sm font-medium">Database</span>
                  {isEditing ? (
                    <Switch
                      checked={currentSettings.dbBackupEnabled ?? true}
                      onCheckedChange={(v) => setEditedSettings({ ...editedSettings, dbBackupEnabled: v })}
                      className="ml-auto scale-75"
                    />
                  ) : (
                    <Badge variant="outline" className={`ml-auto text-xs ${currentSettings.dbBackupEnabled ? 'text-green-400' : 'text-gray-500'}`}>
                      {currentSettings.dbBackupEnabled ? 'ON' : 'OFF'}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Priority 1 - Critical</p>
              </div>
              <div className={`p-3 rounded-lg border ${currentSettings.htmlBackupEnabled ? 'border-blue-500/50 bg-blue-500/10' : 'border-slate-700 bg-slate-800/50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-4 w-4 text-blue-400" />
                  <span className="text-sm font-medium">HTML</span>
                  {isEditing ? (
                    <Switch
                      checked={currentSettings.htmlBackupEnabled ?? true}
                      onCheckedChange={(v) => setEditedSettings({ ...editedSettings, htmlBackupEnabled: v })}
                      className="ml-auto scale-75"
                    />
                  ) : (
                    <Badge variant="outline" className={`ml-auto text-xs ${currentSettings.htmlBackupEnabled ? 'text-green-400' : 'text-gray-500'}`}>
                      {currentSettings.htmlBackupEnabled ? 'ON' : 'OFF'}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Priority 2 - Project snapshots</p>
              </div>
              <div className={`p-3 rounded-lg border ${currentSettings.zipBackupEnabled ? 'border-orange-500/50 bg-orange-500/10' : 'border-slate-700 bg-slate-800/50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Archive className="h-4 w-4 text-orange-400" />
                  <span className="text-sm font-medium">ZIP</span>
                  {isEditing ? (
                    <Switch
                      checked={currentSettings.zipBackupEnabled ?? false}
                      onCheckedChange={(v) => setEditedSettings({ ...editedSettings, zipBackupEnabled: v })}
                      className="ml-auto scale-75"
                    />
                  ) : (
                    <Badge variant="outline" className={`ml-auto text-xs ${currentSettings.zipBackupEnabled ? 'text-green-400' : 'text-gray-500'}`}>
                      {currentSettings.zipBackupEnabled ? 'ON' : 'OFF'}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Priority 3 - Archive exports</p>
              </div>
            </div>
            {isEditing && (
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSaveSettings}
                  disabled={updateSettingsMutation.isPending}
                  data-testid="button-save-settings"
                >
                  {updateSettingsMutation.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Save Changes
                </Button>
              </div>
            )}
          </div>

          {isEditing && (
            <>
              <Separator className="my-4 bg-slate-700" />
              <div className="space-y-4">
                <p className="text-sm font-medium">Advanced Settings</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Start Time (CET)</Label>
                    <div className="flex gap-1 mt-1">
                      <Select
                        value={String(currentSettings.windowStartHour ?? 2)}
                        onValueChange={(v) => setEditedSettings({ ...editedSettings, windowStartHour: parseInt(v) })}
                      >
                        <SelectTrigger className="w-16 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-muted-foreground self-center">:</span>
                      <Select
                        value={String(currentSettings.windowStartMinute ?? 30)}
                        onValueChange={(v) => setEditedSettings({ ...editedSettings, windowStartMinute: parseInt(v) })}
                      >
                        <SelectTrigger className="w-16 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => (
                            <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">End Time (CET)</Label>
                    <div className="flex gap-1 mt-1">
                      <Select
                        value={String(currentSettings.windowEndHour ?? 6)}
                        onValueChange={(v) => setEditedSettings({ ...editedSettings, windowEndHour: parseInt(v) })}
                      >
                        <SelectTrigger className="w-16 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-muted-foreground self-center">:</span>
                      <Select
                        value={String(currentSettings.windowEndMinute ?? 0)}
                        onValueChange={(v) => setEditedSettings({ ...editedSettings, windowEndMinute: parseInt(v) })}
                      >
                        <SelectTrigger className="w-16 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => (
                            <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Random Jitter (min)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={60}
                      value={currentSettings.jitterMinutes ?? 15}
                      onChange={(e) => setEditedSettings({ ...editedSettings, jitterMinutes: parseInt(e.target.value) || 0 })}
                      className="h-8 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max Server Lag (ms)</Label>
                    <Input
                      type="number"
                      min={50}
                      max={1000}
                      step={50}
                      value={currentSettings.maxLagMs ?? 200}
                      onChange={(e) => setEditedSettings({ ...editedSettings, maxLagMs: parseInt(e.target.value) || 200 })}
                      className="h-8 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">HTML History Keep</Label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={currentSettings.htmlHistoryKeep ?? 10}
                      onChange={(e) => setEditedSettings({ ...editedSettings, htmlHistoryKeep: parseInt(e.target.value) || 10 })}
                      className="h-8 mt-1"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Files to keep in history per project</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* HTML Backup Cache Health (task #216) */}
      <Card className="bg-slate-900 border-slate-700" data-testid="cache-health-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-base">HTML Backup Cache Health</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Per-project freshness and fragment cache coverage
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <Separator className="bg-slate-700" />
        <CardContent className="pt-4 space-y-4">
          <div className="grid grid-cols-6 gap-3">
            <div className="text-center p-2 bg-slate-800/50 rounded" data-testid="tile-total">
              <p className="text-xl font-bold text-slate-200">{cacheHealth?.totals.projects ?? 0}</p>
              <p className="text-xs text-muted-foreground">Total projects</p>
            </div>
            <div className="text-center p-2 bg-slate-800/50 rounded" data-testid="tile-fresh">
              <p className="text-xl font-bold text-green-400">{cacheHealth?.totals.fresh ?? 0}</p>
              <p className="text-xs text-muted-foreground">Fresh</p>
            </div>
            <div className="text-center p-2 bg-slate-800/50 rounded" data-testid="tile-pending">
              <p className="text-xl font-bold text-slate-300">{cacheHealth?.totals.pending ?? 0}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
            <div className="text-center p-2 bg-slate-800/50 rounded" data-testid="tile-overdue">
              <p className="text-xl font-bold text-orange-400">{cacheHealth?.totals.overdue ?? 0}</p>
              <p className="text-xs text-muted-foreground">Overdue</p>
            </div>
            <div className="text-center p-2 bg-slate-800/50 rounded" data-testid="tile-failed">
              <p className="text-xl font-bold text-red-400">{cacheHealth?.totals.failed ?? 0}</p>
              <p className="text-xs text-muted-foreground">Failed</p>
            </div>
            <div className="text-center p-2 bg-slate-800/50 rounded" data-testid="tile-coverage">
              <p className="text-xl font-bold text-blue-400">
                {cacheHealth
                  ? cacheHealth.coverage.totalPulses === 0
                    ? '—'
                    : `${Math.round((cacheHealth.coverage.cachedFragments / cacheHealth.coverage.totalPulses) * 100)}%`
                  : '—'}
              </p>
              <p className="text-xs text-muted-foreground">
                Coverage{' '}
                {cacheHealth && (
                  <span className="text-[10px]">
                    ({cacheHealth.coverage.cachedFragments}/{cacheHealth.coverage.totalPulses})
                  </span>
                )}
              </p>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Unhealthy Projects</p>
            {!cacheHealth ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : cacheHealth.unhealthy.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                All projects healthy.
              </p>
            ) : (
              <ScrollArea className="h-40">
                <div className="space-y-1">
                  {cacheHealth.unhealthy.map((row) => (
                    <div
                      key={row.projectId}
                      className="flex items-center gap-2 p-2 hover:bg-slate-800/50 rounded text-sm"
                      data-testid={`unhealthy-${row.projectId}`}
                    >
                      <Badge
                        variant="outline"
                        className={`text-xs whitespace-nowrap ${
                          row.tier === 'failed'
                            ? 'border-red-500 text-red-400'
                            : 'border-orange-500 text-orange-400'
                        }`}
                      >
                        {row.tier}
                      </Badge>
                      <span className="font-mono text-xs text-muted-foreground">{row.projectId}</span>
                      <span className="truncate text-xs flex-1">{row.projectName ?? ''}</span>
                      <span
                        className="text-[10px] text-muted-foreground whitespace-nowrap"
                        data-testid={`reason-${row.projectId}`}
                      >
                        {reasonLabel(row.reason)}
                      </span>
                      <span
                        className="text-[10px] text-muted-foreground whitespace-nowrap"
                        title="Last activity"
                        data-testid={`last-activity-${row.projectId}`}
                      >
                        {row.lastActivityAt
                          ? `act ${formatDistanceToNow(new Date(row.lastActivityAt), { addSuffix: true })}`
                          : 'no activity'}
                      </span>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap" title="Last successful backup">
                        {row.lastSuccessfulBackupAt
                          ? `ok ${formatDistanceToNow(new Date(row.lastSuccessfulBackupAt), { addSuffix: true })}`
                          : 'never ok'}
                      </span>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap" title="Coverage">
                        {row.coverage.cached}/{row.coverage.total}
                        {row.coveragePct !== null && ` (${row.coveragePct}%)`}
                      </span>
                      {row.lastErrorMessage && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <AlertTriangle className="h-3 w-3 text-red-400" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">{row.lastErrorMessage}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[10px] border-emerald-600 text-emerald-300 hover:bg-emerald-900/30"
                        onClick={() => triggerBackupMutation.mutate({ backupType: 'html', projectId: row.projectId })}
                        disabled={triggerBackupMutation.isPending}
                        data-testid={`run-backup-now-${row.projectId}`}
                      >
                        Run backup now
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Timer className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle className="text-base">Backup Queue</CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Backups run one at a time to prevent conflicts
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => triggerBackupMutation.mutate({ backupType: 'database' })}
                      disabled={triggerBackupMutation.isPending}
                      data-testid="button-trigger-db-backup"
                    >
                      <Database className="h-3 w-3 mr-1" />
                      DB
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Queue database backup</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => triggerHtmlAllMutation.mutate()}
                      disabled={triggerHtmlAllMutation.isPending}
                      data-testid="button-trigger-html-all"
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      HTML All
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Queue HTML backup for all projects (ordered by last activity)</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="outline"
                size="sm"
                onClick={() => processQueueMutation.mutate()}
                disabled={processQueueMutation.isPending || (queueStatus?.queue.pending ?? 0) === 0}
                data-testid="button-process-queue"
              >
                <Zap className="h-3 w-3 mr-1" />
                {processQueueMutation.isPending ? 'Processing...' : 'Process Queue'}
              </Button>
              {(queueStatus?.queue.pending ?? 0) > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => cancelAllMutation.mutate()}
                  disabled={cancelAllMutation.isPending}
                  className="text-red-400 hover:text-red-300"
                  data-testid="button-cancel-all"
                >
                  <Ban className="h-3 w-3 mr-1" />
                  Cancel All
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <Separator className="bg-slate-700" />
        <CardContent className="pt-4">
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="text-center p-2 bg-slate-800/50 rounded">
              <p className="text-xl font-bold text-yellow-400">{queueStatus?.queue.pending ?? 0}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
            <div className="text-center p-2 bg-slate-800/50 rounded">
              <p className="text-xl font-bold text-blue-400">{queueStatus?.queue.running ?? 0}</p>
              <p className="text-xs text-muted-foreground">Running</p>
            </div>
            <div className="text-center p-2 bg-slate-800/50 rounded">
              <p className="text-xl font-bold text-green-400">{queueStatus?.queue.completedToday ?? 0}</p>
              <p className="text-xs text-muted-foreground">Completed Today</p>
            </div>
            <div className="text-center p-2 bg-slate-800/50 rounded">
              <p className="text-xl font-bold text-red-400">{queueStatus?.queue.failedToday ?? 0}</p>
              <p className="text-xs text-muted-foreground">Failed Today</p>
            </div>
          </div>

          {queueStatus?.currentlyRunning && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-4">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                <span className="font-medium">Currently Running:</span>
                <BackupTypeBadge type={queueStatus.currentlyRunning.backupType as QueueItem['backupType']} />
                {queueStatus.currentlyRunning.projectId && (
                  <span className="text-sm text-muted-foreground">
                    Project: {queueStatus.currentlyRunning.projectId}
                  </span>
                )}
                <span className="text-xs text-muted-foreground ml-auto">
                  Started {formatDistanceToNow(new Date(queueStatus.currentlyRunning.startedAt), { addSuffix: true })}
                </span>
              </div>
            </div>
          )}

          <div>
            <p className="text-sm font-medium mb-2">Recent Queue Activity</p>
            {historyLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ScrollArea className="h-48">
                <div className="space-y-1">
                  {(queueHistory || []).slice(0, 20).map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center gap-2 p-2 hover:bg-slate-800/50 rounded text-sm"
                      data-testid={`queue-item-${item.id}`}
                    >
                      <BackupTypeBadge type={item.backupType} />
                      <QueueStatusBadge status={item.status} />
                      {item.projectId && (
                        <span className="text-xs text-muted-foreground truncate max-w-24">
                          {item.projectId}
                        </span>
                      )}
                      {item.metadata?.imageCacheStats && (
                        (item.metadata.imageCacheStats.cached
                          + item.metadata.imageCacheStats.reUploaded
                          + item.metadata.imageCacheStats.downloaded
                          + item.metadata.imageCacheStats.gc) > 0 && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="text-[10px] text-muted-foreground font-mono whitespace-nowrap"
                                  data-testid={`img-cache-stats-${item.id}`}
                                >
                                  img: {item.metadata.imageCacheStats.cached}c
                                  /{item.metadata.imageCacheStats.reUploaded}r
                                  /{item.metadata.imageCacheStats.downloaded}d
                                  /{item.metadata.imageCacheStats.gc}g
                                  {item.metadata.imageCacheStats.errors > 0 && (
                                    <span className="text-red-400">/{item.metadata.imageCacheStats.errors}e</span>
                                  )}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="text-xs">
                                <div>cached (skipped re-upload): {item.metadata.imageCacheStats.cached}</div>
                                <div>re-uploaded (source changed): {item.metadata.imageCacheStats.reUploaded}</div>
                                <div>downloaded (cold start): {item.metadata.imageCacheStats.downloaded}</div>
                                <div>GC'd (orphaned): {item.metadata.imageCacheStats.gc}</div>
                                {item.metadata.imageCacheStats.errors > 0 && (
                                  <div className="text-red-400">errors: {item.metadata.imageCacheStats.errors}</div>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )
                      )}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {item.queuedAt ? format(new Date(item.queuedAt), 'MMM d, HH:mm') : '--'}
                      </span>
                      {item.errorMessage && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <AlertTriangle className="h-3 w-3 text-red-400" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">{item.errorMessage}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  ))}
                  {(!queueHistory || queueHistory.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No backup activity yet
                    </p>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
